import { KeyValuePipe } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import axios from 'axios';
import { RefDirective } from 'rete-angular-plugin/20';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { StateNode } from '../state-node/state-node';
import { WorkflowNotification } from '../types';
import { WorkflowEditorService } from '../workflow-editor.service';
import { TransitionNode } from './transition-node';

@Component({
  selector: 'app-transition-node',
  templateUrl: './transition-node.component.html',
  styleUrls: ['./transition-node.component.scss'],
  imports: [
    MatIcon,
    RefDirective,
    PayeeMiniIconButtonComponent,
    KeyValuePipe,
  ],
  host: {
    '[class.disabled]': 'isDisabled()',
  },
})
export class TransitionNodeComponent
  implements OnInit, OnDestroy, OnChanges, AfterContentInit
{
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private workflowEditorService = inject(WorkflowEditorService);
  private snackbar = inject(MatSnackBar);

  private forceExpandSub?: Subscription;
  private mouseX?: number;
  private mouseY?: number;

  @Input({ required: true }) data!: TransitionNode;
  @Input({ required: true }) emit!: (data: unknown) => void;
  @Input({ required: true }) rendered!: () => void;

  seed = 0;
  showDetails = false;

  constructor() {
    this.cdr.detach();
  }

  isDisabled(): boolean {
    const fromStateActive =
      !this.data.transition.from_process_states ||
      this.data.from.some((s) => s.is_active);

    return !fromStateActive;
  }

  @HostListener('mousedown', ['$event']) onMouseDown(event: MouseEvent) {
    this.mouseX = event.screenX;
    this.mouseY = event.screenY;
  }

  @HostListener('click', ['$event']) onClick(event: PointerEvent) {
    if (
      event.screenX !== this.mouseX ||
      event.screenY !== this.mouseY ||
      !this.data.to ||
      this.isDisabled()
    ) {
      return;
    }

    for (const node of this.data.editor.getNodes()) {
      if (
        node instanceof StateNode &&
        (!node.state.is_start || this.data.to.state.is_start)
      ) {
        node.is_active = false;
        this.workflowEditorService.stateChange.next({
          node,
          selected: false,
        });
      }
    }

    this.data.to.is_active = true;
    this.workflowEditorService.stateChange.next({
      node: this.data.to,
      selected: true,
    });
  }

  ngOnInit(): void {
    this.setTransitionDetails(false);
    this.forceExpandSub = this.workflowEditorService.forceExpand.subscribe(
      (e) => {
        if (e !== undefined) {
          this.setTransitionDetails(e);
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.forceExpandSub?.unsubscribe();
  }

  ngOnChanges(): void {
    this.forceRender();
  }

  ngAfterContentInit(): void {
    this.data.height = this.elementRef.nativeElement.clientHeight;
    this.data.width = this.elementRef.nativeElement.clientWidth;
  }

  setTransitionDetails(showDetails: boolean): void {
    this.showDetails = showDetails;
    this.forceRender();
    setTimeout(() => {
      this.data.height = this.elementRef.nativeElement.clientHeight;
      this.data.width = this.elementRef.nativeElement.clientWidth;
    });
  }

  toggleTransitionDetails(event: MouseEvent): void {
    console.log('toggle transition details');
    event.stopPropagation();
    event.preventDefault();
    this.showDetails = !this.showDetails;
    this.workflowEditorService.forceExpand.next(undefined);
    this.forceRender();
    setTimeout(() => {
      this.data.height = this.elementRef.nativeElement.clientHeight;
      this.data.width = this.elementRef.nativeElement.clientWidth;
    });
  }

  async copyNodeText(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    event.preventDefault();

    try {
      await navigator.clipboard.writeText(this.data.transition.label);
      this.snackbar.open('Címke a vágólapra másolva', 'OK', { duration: 3000 });
    } catch (error) {
      console.error('Failed to copy node label to clipboard', error);
    }
  }

  async previewNotification(
    event: MouseEvent,
    notification: WorkflowNotification,
  ): Promise<void> {
    event.stopPropagation();
    try {
      const result = await axios.post(
        `${environment.baseUrl}/api/workflow-process/preview-notification`,
        {
          notification: notification.id,
          transition: this.data.transition.class_name,
        },
      );

      window.open(
        URL.createObjectURL(new Blob([result.data], { type: 'text/html' })),
        '_blank',
      );
    } catch (error) {
      console.error('Error while notification preview', error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'Értesítés előnézet betöltése sikertelen.';
      this.snackbar.open(message, 'OK', { duration: 5000 });
    }
  }

  private forceRender() {
    this.cdr.detectChanges();
    requestAnimationFrame(() => this.rendered());
    this.seed++;
  }
}
